import React, { useState, useEffect, useRef } from "react";
import { FiChevronRight } from "react-icons/fi";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faAddressBook } from '@fortawesome/free-solid-svg-icons';
import { faListCheck } from '@fortawesome/free-solid-svg-icons'

// Load reCAPTCHA v3 script
const loadRecaptcha = () => {
  if (window.grecaptcha) return;
  const script = document.createElement('script');
  script.src = `https://www.google.com/recaptcha/api.js?render=${process.env.REACT_APP_RECAPTCHA_SITE_KEY}`;
  document.head.appendChild(script);
};
const validateEmail = (email) => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

const validatePhone = (phone) => {
  const phoneRegex = /^\d{10}$/;
  return phoneRegex.test(phone.replace(/\D/g, ""));
};

const encodeForDisplay = (value) => {
  if (value === null || value === undefined) return "";
  return String(value)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/\"/g, "&quot;")
    .replace(/'/g, "&#39;");
};

const encodeErrorMap = (errorMap) => {
  if (!errorMap || typeof errorMap !== "object") return {};
  return Object.fromEntries(
    Object.entries(errorMap).map(([key, value]) => [
      key === "phone_number" ? "phone" : key,
      Array.isArray(value)
        ? encodeForDisplay(value.join(" "))
        : encodeForDisplay(value),
    ])
  );
};

const GetInTouchButton = () => {
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    email: "",
    city: "",
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const modalRef = useRef(null);
  const nameInputRef = useRef(null);
  const closeButtonRef = useRef(null);

  const phoneInputRef = useRef(null);
  const emailInputRef = useRef(null);
  const cityInputRef = useRef(null);
  const submitButtonRef = useRef(null);

  useEffect(() => {
    if (showForm && nameInputRef.current) {
      nameInputRef.current.focus();
    }
  }, [showForm]);

  const handleModalKeyDown = (e) => {
    if (e.key === 'Escape') {
      setShowForm(false);
      return;
    }

    // Focus trap for Tab key
    if (e.key === 'Tab') {
      const focusableElements = [
        closeButtonRef.current,
        nameInputRef.current,
        phoneInputRef.current,
        emailInputRef.current,
        cityInputRef.current,
        submitButtonRef.current,
      ].filter(el => el !== null);

      const currentIndex = focusableElements.indexOf(document.activeElement);
      let nextIndex;

      if (e.shiftKey) {
        // Shift + Tab: move to previous
        nextIndex = currentIndex === 0 ? focusableElements.length - 1 : currentIndex - 1;
      } else {
        // Tab: move to next
        nextIndex = currentIndex === focusableElements.length - 1 ? 0 : currentIndex + 1;
      }

      e.preventDefault();
      focusableElements[nextIndex].focus();
    }

    // Arrow key navigation
    if (e.key === 'ArrowDown' || e.key === 'ArrowRight') {
      const inputElements = [nameInputRef.current, phoneInputRef.current, emailInputRef.current, cityInputRef.current];
      const currentIndex = inputElements.indexOf(document.activeElement);
      if (currentIndex !== -1 && currentIndex < inputElements.length - 1) {
        e.preventDefault();
        inputElements[currentIndex + 1].focus();
      }
    }

    if (e.key === 'ArrowUp' || e.key === 'ArrowLeft') {
      const inputElements = [nameInputRef.current, phoneInputRef.current, emailInputRef.current, cityInputRef.current];
      const currentIndex = inputElements.indexOf(document.activeElement);
      if (currentIndex > 0) {
        e.preventDefault();
        inputElements[currentIndex - 1].focus();
      }
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    if (errors[e.target.name]) {
      setErrors({ ...errors, [e.target.name]: "" });
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = "Name is required";
    }

    if (!formData.email.trim()) {
      newErrors.email = "Email is required";
    } else if (!validateEmail(formData.email)) {
      newErrors.email = "Please enter a valid email address";
    }

    if (!formData.phone.trim()) {
      newErrors.phone = "Phone number is required";
    } else if (!validatePhone(formData.phone)) {
      newErrors.phone = "Please enter a valid 10-digit phone number";
    }

    if (!formData.city.trim()) {
      newErrors.city = "City is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      // Load reCAPTCHA script if not already loaded
      loadRecaptcha();

      // Wait for grecaptcha to be available
      let retries = 0;
      while (!window.grecaptcha && retries < 10) {
        await new Promise(resolve => setTimeout(resolve, 100));
        retries++;
      }

      if (!window.grecaptcha) {
        throw new Error('reCAPTCHA failed to load');
      }

      // Wait for reCAPTCHA runtime readiness before execute
      const recaptchaToken = await new Promise((resolve, reject) => {
        const timeoutId = window.setTimeout(() => {
          reject(new Error('reCAPTCHA initialization timed out'));
        }, 5000);

        window.grecaptcha.ready(async () => {
          try {
            const token = await window.grecaptcha.execute(
              process.env.REACT_APP_RECAPTCHA_SITE_KEY,
              { action: 'contact_form_submission' }
            );
            window.clearTimeout(timeoutId);
            resolve(token);
          } catch (err) {
            window.clearTimeout(timeoutId);
            reject(err);
          }
        });
      });

      // Call backend API
      const apiUrl = `${process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000'}/api/contact-form`;
      const response = await fetch(apiUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          phone_number: formData.phone,
          city: formData.city,
          recaptcha_token: recaptchaToken,
        }),
      });

      const responseData = await response.json();

      if (!response.ok) {
        // Handle server-side validation errors
        if (responseData.errors) {
          setErrors(encodeErrorMap(responseData.errors));
          toast.error(encodeForDisplay(responseData.message || "Please fix the errors below"));
        } else {
          throw new Error(encodeForDisplay(responseData.message || "Failed to submit form"));
        }
        setIsSubmitting(false);
        return;
      }

      setFormData({
        name: "",
        phone: "",
        email: "",
        city: "",
      });
      setErrors({});
      setShowForm(false);
      toast.success("Form submitted successfully!");
    } catch (error) {
      console.error("Error:", error);
      toast.error(encodeForDisplay(error.message || "An error occurred. Please try again."));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (

    <div>
          <ToastContainer />
          <div className="get-in-touch-container">
  <button
    className="button"
    aria-haspopup="dialog"
    aria-label="Get in Touch, opens contact form"
    title="Get in Touch"
    onClick={() => {
      setShowForm(true);
      setFormData({
        name: '',
        phone: '',
        email: '',
        city: '',
      });
    }}
  >
    <FontAwesomeIcon
      icon={faAddressBook}
      style={{ fontSize: '24px', marginRight: '8px' }}
      aria-hidden="true"
    />
  </button>
  <span className="button-title">Get in Touch</span>
</div>

    
 
<div className="Invest">
  <a
    href="/onboarding/"
    className="invest-link"
    target="_blank"
    rel="noopener noreferrer"
    aria-label="Invest Directly, opens in new tab"
    title="Invest Directly - Opens in new tab"
  >
    <FontAwesomeIcon
      icon={faListCheck}
      className="invest-icon"
    />
  </a>
  <span className="invest-title">Invest Directly </span>
</div>

      {/* Modal/Popup Form */}
      {showForm && (
        <div
          ref={modalRef}
          className="modal"
          style={{ display: showForm ? "block" : "none" }}
          onKeyDown={handleModalKeyDown}
        >
          <div
            className="modal-content"
            role="dialog"
            aria-modal="true"
            aria-labelledby="get-in-touch-dialog-title"
          >
            <button
              ref={closeButtonRef}
              className="close"
              onClick={() => setShowForm(false)}
              aria-label="Close contact form dialog"
              title="Close"
              type="button"
            >
              &times;
            </button>
            <h2
              id="get-in-touch-dialog-title"
              style={{
                textAlign: "center",
                fontWeight: "bolder",
                fontFamily: "Dancing Script",
                color: "#212529",
                marginBottom: "10px",
                fontFamily: "Montserrat, sans-serif",
              }}
            >
              Get in Touch
            </h2>
            <form style={{fontFamily: "Montserrat, sans-serif",}} onSubmit={handleSubmit}>
              <div style={{ marginBottom: "15px" }}>
                <label htmlFor="name-input">
                  Name
                </label>
                <input
                  ref={nameInputRef}
                  id="name-input"
                  type="text"
                  placeholder="Enter your full name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  aria-label="Full name"
                  aria-describedby={errors.name ? "name-error" : undefined}
                  style={{ borderColor: errors.name ? "red" : "", width: "100%" }}
                />
                {errors.name && <span id="name-error" style={{ color: "red", fontSize: "12px" }}>{errors.name}</span>}
              </div>
              <div style={{ marginBottom: "15px" }}>
                <label htmlFor="phone-input">
                  Phone Number
                </label>
                <input
                  ref={phoneInputRef}
                  id="phone-input"
                  type="tel"
                  placeholder="Enter 10-digit phone number"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  aria-label="Phone number (10 digits)"
                  aria-describedby={errors.phone ? "phone-error" : undefined}
                  style={{ borderColor: errors.phone ? "red" : "", width: "100%" }}
                />
                {errors.phone && <span id="phone-error" style={{ color: "red", fontSize: "12px" }}>{errors.phone}</span>}
              </div>
              <div style={{ marginBottom: "15px" }}>
                <label htmlFor="email-input">
                  Email
                </label>
                <input
                  ref={emailInputRef}
                  id="email-input"
                  type="email"
                  placeholder="Enter your email address"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  aria-label="Email address"
                  aria-describedby={errors.email ? "email-error" : undefined}
                  style={{ borderColor: errors.email ? "red" : "", width: "100%" }}
                />
                {errors.email && <span id="email-error" style={{ color: "red", fontSize: "12px" }}>{errors.email}</span>}
              </div>
              <div style={{ marginBottom: "15px" }}>
                <label htmlFor="city-input">
                  City
                </label>
                <input
                  ref={cityInputRef}
                  id="city-input"
                  type="text"
                  placeholder="Enter your city"
                  name="city"
                  value={formData.city}
                  onChange={handleChange}
                  aria-label="City"
                  aria-describedby={errors.city ? "city-error" : undefined}
                  style={{ borderColor: errors.city ? "red" : "", width: "100%" }}
                />
                {errors.city && <span id="city-error" style={{ color: "red", fontSize: "12px" }}>{errors.city}</span>}
              </div>
              {/* Setting text-align to center */}
              <div style={{ alignItems: "center", marginLeft: "10%" }}>
                <button
                  ref={submitButtonRef}
                  type="submit"
                  className="get-in-touch-form-submit"
                  disabled={isSubmitting}
                  aria-label={isSubmitting ? "Submitting contact form" : "Submit contact form"}
                  style={{
                    backgroundColor: "#033132",
                    color: "white",
                    padding: "8px 20px",
                    borderRadius: "0.25rem",
                    fontFamily: "Montserrat, sans-serif",
                    opacity: isSubmitting ? 0.6 : 1,
                    cursor: isSubmitting ? "not-allowed" : "pointer",
                    border: "2px solid transparent",
                    fontSize: "14px",
                    fontWeight: "500",
                  }}
                >
                  {isSubmitting ? "Submitting..." : "Submit"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default GetInTouchButton;
